const API_BASE = "/api";

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body.error || `Request failed: ${res.status}`;
    if (res.status === 401 && typeof window !== "undefined" && !res.url.includes("/auth/")) {
      // Schwab token expired — redirect to broker connection page
      window.dispatchEvent(new CustomEvent("schwab:expired", { detail: msg }));
    }
    throw new Error(msg);
  }
  return res.json();
}

export async function apiGetQuiet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  return handleResponse(res);
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  return handleResponse(res);
}

/** GET that supports conditional ?ver= responses. Returns null on 304. */
export async function apiGetMaybe<T>(path: string): Promise<T | null> {
  const res = await fetch(`${API_BASE}${path}`);
  if (res.status === 304) return null;
  return handleResponse(res);
}

export async function apiPut<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiPost<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { method: "DELETE" });
  return handleResponse(res);
}

export async function apiPostQuiet<T>(path: string, data: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

/** Subscribe to an SSE stream endpoint. Calls onEvent for each named event.
 *  Returns an abort function to cancel the stream. */
export function apiSSE(
  path: string,
  onEvent: (event: string, data: unknown) => void,
  onError?: (err: Error) => void,
): () => void {
  const ctrl = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API_BASE}${path}`, { signal: ctrl.signal });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed: ${res.status}`);
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let currentEvent = "message";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("event:")) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            const raw = line.slice(5).trim();
            try {
              onEvent(currentEvent, JSON.parse(raw));
            } catch {
              onEvent(currentEvent, raw);
            }
            currentEvent = "message";
          }
        }
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        onError?.(e instanceof Error ? e : new Error(String(e)));
      }
    }
  })();
  return () => ctrl.abort();
}

type ToastFn = (type: "success" | "error", msg: string) => void;

// --- Reconciliation API helpers ---

import type { ReconReport } from "@/app/strategies/components/reconciliation-types";

/** Fetch the unified option position reconciliation report. */
export const fetchReconciliation = (accountHash: string) =>
  apiGet<ReconReport>(`/positions/reconciliation?accountHash=${encodeURIComponent(accountHash)}`);

export const reconMarkClosed = (positionId: number, exitPrice: number, closedAt: string, exitType: string) =>
  apiPost<{ ok: boolean }>("/positions/recon/mark-closed", { positionId, exitPrice, closedAt, exitType });

export const reconUpdateQty = (positionId: number, qty: number) =>
  apiPost<{ ok: boolean }>("/positions/recon/update-qty", { positionId, qty });

export const reconDelete = (positionId: number) =>
  apiPost<{ ok: boolean }>("/positions/recon/delete", { positionId });

export interface ReconCreateFromFillBody {
  accountHash: string;
  symbol: string;
  underlyingSymbol: string;
  putCall: string;
  strikePrice: number;
  expirationDate: string;
  qty: number;
  price: number;
  openedAt: string;
  orderId?: string | null;
}
export const reconCreateFromFill = (body: ReconCreateFromFillBody) =>
  apiPost<{ ok: boolean }>("/positions/recon/create-from-fill", body);

/** Wraps a try/ok-check/catch pattern: calls `fn`, toasts success or error. Returns true on success. */
/** A broker account in the legacy `{hashValue, ...}` shape, sourced from the
 *  unified cross-broker `/accounts` endpoint (Schwab + Paper). Use this instead
 *  of `/schwab/accounts` so paper accounts appear everywhere accounts are listed. */
export interface UiAccount {
  accountNumber: string;
  hashValue: string;
  nickname: string | null;
  /** User-set name used in push notifications (Accounts page). */
  notifyNickname: string | null;
  tokenId: number;
  loginLabel: string;
  paper: boolean;
  /** "schwab" | "tastytrade" | "paper" | "tda" from GET /accounts. */
  broker?: string;
  /** Paper only: "schwab" | "tastytrade", the first real account's broker. */
  dataBroker?: string | null;
  cashBalance?: number;
  stale: boolean;
}

export async function fetchAccounts(): Promise<UiAccount[]> {
  const rows = await apiGet<{
    accountHash: string;
    accountNumber: string | null;
    nickname: string | null;
    notifyNickname: string | null;
    tokenId: number;
    loginLabel: string;
    paper: boolean;
    broker?: string;
    dataBroker?: string | null;
    cashBalance: number | null;
    label: string;
    stale: boolean;
  }[]>("/accounts");
  return rows.map((u) => ({
    accountNumber: u.accountNumber ?? u.label,
    hashValue: u.accountHash,
    nickname: u.nickname,
    notifyNickname: u.notifyNickname ?? null,
    tokenId: u.tokenId,
    loginLabel: u.loginLabel,
    paper: u.paper,
    broker: u.broker,
    dataBroker: u.dataBroker ?? null,
    cashBalance: u.cashBalance ?? undefined,
    stale: u.stale,
  }));
}

export async function apiAction(
  fn: () => Promise<{ ok: boolean; error?: string }>,
  successMsg: string,
  showToast: ToastFn,
): Promise<boolean> {
  try {
    const res = await fn();
    if (res.ok) { showToast("success", successMsg); return true; }
    showToast("error", res.error ?? "Failed");
    return false;
  } catch (e) {
    showToast("error", e instanceof Error ? e.message : "Failed");
    return false;
  }
}
